/**
 * MyClips V3 — Design Tokens (L-1 Light UI)
 *
 * Logo-driven brand palette: Electric Blue → Royal Purple → Magenta-Violet.
 * Light default + dark twin. JS karşılıklar — Framer Motion, inline style
 * ve runtime theme toggler için. globals.css bu değerleri CSS variable
 * olarak zaten ihraç ediyor; JS import'u SADECE imperative context
 * gerektiğinde kullan (motion spring config, programmatic color math).
 */

export const color = {
  /* Light-first neutral ladder (0 en açık, 11 en koyu) */
  gray: {
    0:  "#FFFFFF",
    1:  "#FAFBFF",
    2:  "#F3F4FA",
    3:  "#E8E6F0",
    4:  "#D4D0E2",
    5:  "#B3AEC5",
    6:  "#9691A8",
    7:  "#6E6985",
    8:  "#5B5670",
    9:  "#3D384F",
    10: "#1C1830",
    11: "#0F0B1E",
  },

  /* Dark theme neutral ladder (mor underbase) */
  grayDark: {
    0:  "#0A0713",
    1:  "#120D1F",
    2:  "#1B1531",
    3:  "#251E3D",
    4:  "#3A3252",
    5:  "#4A4168",
    6:  "#6E6685",
    7:  "#8A83A0",
    8:  "#A8A0BD",
    9:  "#C4BCDE",
    10: "#E2DEF0",
    11: "#F2EFFA",
  },

  /* Brand gradient stops (logo'dan okunan) */
  brand: {
    blue:    "#2952FF",
    purple:  "#5B2DD8",
    magenta: "#B84FF0",
    gradient:
      "linear-gradient(135deg, #2952FF 0%, #5B2DD8 50%, #B84FF0 100%)",
    gradientDark:
      "linear-gradient(135deg, #3D5AFE 0%, #7B3EEC 50%, #D456F5 100%)",
  },

  /* Primary action (backward-compat: accent.dark / accent.light yerine
     accent.primary / accent.primaryDark) */
  accent: {
    primary:     "#5B2DD8",   // light action
    primaryDark: "#9B7BF5",   // dark action
    hover:       "#4A21BE",
    press:       "#3E1AA3",
    soft:        "#EDE6FF",
    glow:        "rgba(91, 45, 216, 0.25)",
    // Celebratory / hero CTA
    alt:         "#C026D3",
    altDeep:     "#9B1BAB",
    // Legacy aliases (eski "Aurum gold" referansları)
    dark:        "#5B2DD8",
    light:       "#4A21BE",
  },

  semantic: {
    success: "#0A7D3E",
    danger:  "#C4302B",
    warning: "#B86E00",
    info:    "#2952FF",
  },

  scrim: {
    bottom:
      "linear-gradient(180deg, transparent 0%, rgba(15,11,30,0) 40%, rgba(15,11,30,0.55) 75%, rgba(15,11,30,0.82) 100%)",
    top:
      "linear-gradient(180deg, rgba(15,11,30,0.35) 0%, transparent 60%)",
    edgeRight:
      "linear-gradient(270deg, rgba(15,11,30,0.35) 0%, transparent 100px)",
    // Media surface (immersive feed) — temadan bağımsız
    mediaTop:
      "linear-gradient(to bottom, rgba(15,11,30,0.35) 0%, transparent 100%)",
    mediaBottom:
      "linear-gradient(to top, rgba(15,11,30,0.55) 0%, rgba(15,11,30,0.20) 45%, transparent 100%)",
  },

  media: {
    fg:          "rgba(255, 255, 255, 0.96)",
    fgSubtle:    "rgba(255, 255, 255, 0.78)",
    textShadow:
      "0 1px 2px rgba(15,11,30,0.45), 0 0 10px rgba(91,45,216,0.25)",
  },
};

export const font = {
  family: {
    primary:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    mono: "'Geist Mono', 'SF Mono', Menlo, Consolas, monospace",
    display:
      "'Fraunces', Georgia, 'Times New Roman', serif",
  },
  size: {
    xs: "11px",
    sm: "13px",
    base: "15px",
    md: "17px",
    lg: "20px",
    xl: "24px",
    "2xl": "32px",
    displaySm: "clamp(28px, 5vw, 40px)",
    displayLg: "clamp(44px, 9vw, 88px)",
  },
  lineHeight: {
    xs: "14px",
    sm: "18px",
    base: "22px",
    md: "24px",
    lg: "26px",
    xl: "30px",
    "2xl": "38px",
    display: "1.05",
    displayTight: "0.98",
  },
  weight: {
    regular: 450,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  features: "'cv11', 'ss01', 'ss03', 'tnum'",
};

export const space = {
  0: "0",
  1: "2px",
  2: "4px",
  3: "6px",
  4: "8px",
  5: "12px",
  6: "16px",
  7: "20px",
  8: "24px",
  9: "32px",
  10: "40px",
  11: "56px",
  12: "80px",
  13: "128px",
};

export const radius = {
  xs: "4px",
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "20px",
  "2xl": "28px",
  pill: "9999px",
  screen: "44px",
};

export const shadow = {
  /* Light default — iki katmanlı (yakın kontakt + uzak ambient) */
  0: "none",
  1: "0 1px 2px rgba(15,11,30,0.04), 0 1px 3px rgba(15,11,30,0.06)",
  2: "0 2px 4px rgba(15,11,30,0.06), 0 6px 16px rgba(15,11,30,0.08)",
  3: "0 4px 12px rgba(15,11,30,0.08), 0 12px 32px rgba(15,11,30,0.12)",
  4: "0 8px 24px rgba(15,11,30,0.12), 0 24px 64px rgba(15,11,30,0.18)",
  5: "0 16px 40px rgba(15,11,30,0.16), 0 48px 120px rgba(15,11,30,0.24)",
  glowAccent:
    "0 0 0 1px rgba(91,45,216,0.30), 0 0 24px rgba(91,45,216,0.25)",
  glowBrand:
    "0 0 0 1px rgba(91,45,216,0.25), 0 0 32px rgba(184,79,240,0.22), 0 0 64px rgba(41,82,255,0.18)",
  hairlineDark:  "inset 0 0 0 1px rgba(255,255,255,0.06)",
  hairlineLight: "inset 0 0 0 1px rgba(15,11,30,0.08)",
  focusRing:     "0 0 0 2px rgba(91, 45, 216, 0.45)",
  focusRingDark: "0 0 0 2px rgba(155, 123, 245, 0.55)",
};

export const blur = {
  /* Light default */
  topbar:  { filter: "blur(18px) saturate(160%)", tint: "rgba(255,255,255,0.72)" },
  sheet:   { filter: "blur(32px) saturate(180%)", tint: "rgba(255,255,255,0.80)" },
  palette: { filter: "blur(40px) saturate(200%)", tint: "rgba(250,251,255,0.85)" },
  chip:    { filter: "blur(12px)",               tint: "rgba(15,11,30,0.28)" },
  /* Media surface (video üstü — temadan bağımsız) */
  media:   { filter: "blur(24px) saturate(140%)", tint: "rgba(15,11,30,0.45)" },
};

export const zIndex = {
  feed: 0,
  overlay: 10,
  rail: 20,
  scrim: 30,
  chip: 40,
  sheet: 100,
  modal: 200,
  toast: 300,
  palette: 400,
};

/* Semantic surface eşlemeleri — runtime theme toggler için referans.
   CSS variable'lar globals.css'te zaten doğru set'liyor; bu objeler
   Framer Motion / canvas render / programmatik stil için. */

export const semanticLight = {
  bgCanvas:       color.gray[0],           // #FFFFFF
  bgSurface:      color.gray[1],           // #FAFBFF
  bgElevated:     color.gray[2],           // #F3F4FA
  fgPrimary:      color.gray[10],          // #1C1830
  fgStrong:       color.gray[11],          // #0F0B1E
  fgMuted:        color.gray[8],           // #5B5670
  fgSubtle:       color.gray[6],           // #9691A8
  fgDisabled:     color.gray[5],
  borderHairline: "rgba(15,11,30,0.08)",
  borderSubtle:   color.gray[3],
  accent:         color.accent.primary,    // #5B2DD8
  accentAlt:      color.accent.alt,        // #C026D3
  brandGradient:  color.brand.gradient,
};

export const semanticDark = {
  bgCanvas:       color.grayDark[0],       // #0A0713
  bgSurface:      color.grayDark[1],       // #120D1F
  bgElevated:     color.grayDark[2],       // #1B1531
  fgPrimary:      color.grayDark[11],      // #F2EFFA
  fgStrong:       "#FFFFFF",
  fgMuted:        color.grayDark[8],       // #A8A0BD
  fgSubtle:       color.grayDark[6],       // #6E6685
  fgDisabled:     color.grayDark[5],
  borderHairline: "rgba(155,123,245,0.10)",
  borderSubtle:   color.grayDark[3],
  accent:         color.accent.primaryDark,// #9B7BF5
  accentAlt:      "#E054F0",
  brandGradient:  color.brand.gradientDark,
};

export const tokens = {
  color,
  font,
  space,
  radius,
  shadow,
  blur,
  zIndex,
  semanticDark,
  semanticLight,
};

export default tokens;
